import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { useParams, withRouter } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import CurrencyInput from 'react-currency-masked-input';
import { isNil } from 'ramda';

import DatePresenter, { SHORT_FORMAT } from '../../../presenters/DatePresenter';
import StorePresenter from '../../../presenters/StorePresenter';
import CashbackPresenter from '../../../presenters/CashbackPresenter';
import FormHeader from '../../../components/FormHeader';
import Group from '../../../components/Group';
import Row from '../../../components/Row';
import Column from '../../../components/Column';
import Flash from '../../../components/forms/Flash';
import { Label, StoreProvider } from '../../../components';
import { createCashback } from '../../../mutations';

const CashbackNewView = (props) => {
  const { lists, relay, history } = props;
  const { fastCashId } = useParams();
  const initialState = {
    fastCashId,
    place: CashbackPresenter.defaultPlace(),
    date: DatePresenter.today(),
    storeId: '',
    purchaseAmount: '',
  };
  const [cashbackParams, setCashbackParams] = useState(initialState);
  const [errors, setErrors] = useState(null);

  const stores = lists.stores.edges.map((e) => e.node);

  const handleCasbhackParamsUpdate = ({ target }) => {
    const { id, value } = target;
    setCashbackParams({ ...cashbackParams, [id]: value });
  };

  const handleAmountUpdate = (_event, amount) => {
    setCashbackParams({ ...cashbackParams, purchaseAmount: amount });
  };

  const handleDateUpdate = (newDate) => {
    setCashbackParams({
      ...cashbackParams,
      date: DatePresenter.shortFormattedDate(newDate),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const paramValues = Object.values(cashbackParams);
    if (paramValues.includes(''))
      return setErrors({ error: 'All fields must be present' });

    return createCashback(relay.environment, cashbackParams, {
      onCompleted: handleSubmitSuccess,
    });
  };

  const handleSubmitSuccess = ({ createCashback }) => {
    const { errorMessage } = createCashback;
    if (errorMessage) return setErrors({ error: errorMessage });

    history.goBack();
  };

  const { date, purchaseAmount, storeId, place } = cashbackParams;
  const options = stores.map((store) => {
    const value = StorePresenter.id(store);
    const selected = value === storeId;
    return {
      label: StorePresenter.name(store),
      value: StorePresenter.id(store),
      selected,
    };
  });

  return (
    <div className="cashbacks">
      <form className="create-cashback-form" onSubmit={handleSubmit}>
        <FormHeader title="Add a cashback">
          <input id="saveCashback" type="submit" defaultValue="Save" />
        </FormHeader>
        {!isNil(errors) && <Flash response={errors} />}
        <Group>
          <Row className="dates">
            <Column className="date">
              <Label attr="purchaseDate" text="Purchase Date" />
              <DatePicker
                selected={DatePresenter.dateObject(date)}
                onChange={(date) => handleDateUpdate(date)}
                dateFormat="MM-dd-yyyy"
              />
            </Column>
            <Column>
              <Label attr="purchaseAmount" text="Purchase Amount" />
              <CurrencyInput
                id="purchaseAmount"
                value={purchaseAmount}
                onChange={handleAmountUpdate}
              />
            </Column>
            <Column>
              <Label attr="place" text="Place" />
              <select
                id="place"
                value={place}
                onChange={handleCasbhackParamsUpdate}
              >
                <option default value="in store">
                  in store
                </option>
                <option value="online">online</option>
                <option value="other">other</option>
              </select>
            </Column>
          </Row>
          <Row>
            <Column className="cashback-field-column">
              <Label attr="store" text="Store" />
              <select
                id="storeId"
                value={storeId}
                onChange={handleCasbhackParamsUpdate}
              >
                <option disabled="disabled" value="">
                  {' '}
                  -- select a store --{' '}
                </option>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Column>
          </Row>
        </Group>
      </form>
    </div>
  );
};

const CashbackNewContainer = createFragmentContainer(CashbackNewView, {
  lists: graphql`
    fragment CashbackNew_lists on Lists {
      stores(first: 1000, active: false) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  `,
});

const CashbackNew = () => {
  return (
    <StoreProvider
      container={CashbackNewContainer}
      modern
      query={graphql`
        query CashbackNewQuery {
          lists {
            ...CashbackNew_lists
          }
        }
      `}
    />
  );
};

export default withRouter(CashbackNew);

CashbackNewView.propTypes = {
  lists: PropTypes.shape({
    stores: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: StorePresenter.shape().isRequired,
        })
      ).isRequired,
    }).isRequired,
  }).isRequired,
  relay: PropTypes.shape().isRequired,
  history: PropTypes.shape().isRequired,
};
